// state/prof-resume.ts
//
// W3 Phase C (Session 3) — Chiedi resume orchestration helper.
//
// Centralises the click-to-resume flow used by the LeftDrawer Cronologia
// list. Order of operations matters and is repeated in tests + mock setups,
// so the orchestration lives in a single export:
//
//   1. Fetch the message thread via GET /api/sessions/:id/chiedi-messages.
//   2. Hydrate the `chiedi` thread in `useChat` with the historical messages.
//   3. Set `chiediSessionId` so subsequent turns reuse the same BE thread.
//   4. Caller navigates to /chiedi.
//
// Why steps 2 + 3 are atomic from the caller's perspective:
//   - If we set the sessionId BEFORE hydrating, a fast re-render of /chiedi
//     could submit a fresh message against an empty thread shape.
//   - If we hydrate BEFORE setting, the very next user turn would post
//     without sessionId and the BE would create a NEW thread.
//   Both are race-free in practice because we await the fetch first, but
//   we still hydrate-then-set so the React reconciler sees both updates
//   in the same tick (Zustand batches them for us).
//
// On fetch failure (404 / 500 / network) the caller decides whether to
// show a toast or navigate elsewhere — this helper rethrows.

import { getToken } from '../api/client';
import type { Message } from '../types/domain';
import { parseProfCitationV1 } from '../types/prof-citation';
import { sessionsApi, type ChiediMessage } from '../api/sessions';
import { useChat } from './chat';
import { figureStateForResume } from './prof-figure';

/** ID used by ProfScreen for its thread. Single source of truth. */
export const PROF_THREAD_ID = 'chiedi';

/**
 * Convert a BE-shape ChiediMessage into the FE Message type. Citations are
 * unknown at the wire level (forward-compat reservation per Phase B); we
 * narrow defensively — anything that doesn't look like a Citation is dropped
 * so a future BE-side citation rollout can't crash an old FE build.
 */
export function toFrontendMessage(m: ChiediMessage, sessionId?: string): Message {
  const authorKind = m.role === 'user' ? 'you' : 'prof';
  const parsedCitations = m.citations.flatMap((value) => {
    const parsed = parseProfCitationV1(value);
    return parsed ? [parsed] : [];
  });
  const citations = parsedCitations.length > 0 ? parsedCitations : undefined;
  return {
    id:         m.id,
    authorKind,
    kind:       'text',
    text:       m.text,
    citations,
    feedbackEligible: m.role === 'assistant',
    checkpointSessionId: m.role === 'assistant' ? sessionId : undefined,
    assistantPersistenceOutcome: m.role === 'assistant' ? 'persisted' : undefined,
  };
}

/**
 * Fetch a Chiedi thread, hydrate the Zustand store, set the cached
 * sessionId. Caller navigates after this resolves.
 *
 * Throws on fetch failure — the caller can surface a toast / bail.
 *
 * @param sessionId  uuid of the sessions row (mode='chiedi').
 * @returns the count of messages loaded — handy for caller analytics.
 */
export async function resumeProfSession(sessionId: string): Promise<number> {
  const capturedToken = getToken();
  const payload = await sessionsApi.getChiediMessages(sessionId);
  if (getToken() !== capturedToken) throw new Error('La sessione è cambiata.');
  const serverMessages: Message[] = payload.messages.map((message) => (
    toFrontendMessage(message, sessionId)
  ));

  // FP10-M1 — same-session resync must never SHRINK the live thread.
  //
  // When the student re-opens the Cronologia drawer (or any path re-resumes the
  // session that is already active), the server view can lag the local thread:
  // the most recent turn may not be persisted yet, or a turn errored before the
  // BE wrote it. A blind overwrite then ERASES a completed Q+answer pair that is
  // present locally ("thread shrank; the old good answer is gone").
  //
  // Guard: if we are resuming the ALREADY-ACTIVE session AND the server view is a
  // strict prefix of the local thread (same ids, in order, just fewer of them),
  // keep the local trailing messages the server hasn't returned yet. A DIFFERENT
  // session, or any diverging history (ids don't line up as a prefix), still
  // overwrites wholesale — that is the correct cross-session / authoritative
  // behaviour and is unchanged.
  const state = useChat.getState();
  const figureContext = figureStateForResume(sessionId, payload.figureContext);
  const localMessages = state.threads[PROF_THREAD_ID]?.messages ?? [];
  let messages = serverMessages;
  if (state.chiediSessionId === sessionId) {
    const withExplicitLocalOnlyTurns = retainExplicitLocalOnlyTurns(serverMessages, localMessages);
    if (withExplicitLocalOnlyTurns) {
      messages = withExplicitLocalOnlyTurns;
    } else if (
      serverMessages.length < localMessages.length
      && isPrefixById(serverMessages, localMessages)
    ) {
      // Server is behind: take its (authoritative) copies of the shared head,
      // then append the local-only tail so no completed turn is lost.
      messages = [...serverMessages, ...localMessages.slice(serverMessages.length)];
    }
  }

  // Two-step state transition: clear (which would ALSO reset chiediSessionId
  // to null for 'chiedi') would race with our setChiediSessionId below. So
  // we use the raw set API instead of clearThread, then setChiediSessionId
  // on the next tick — Zustand batches both synchronous calls.
  //
  // FP3a: resuming a HISTORICAL session has no known follow-up chips (they were
  // never persisted server-side), so reset the persisted chips here — otherwise
  // the previous session's chips would bleed onto the resumed thread.
  useChat.setState((s) => ({
    threads: {
      ...s.threads,
      [PROF_THREAD_ID]: { messages, streaming: false },
    },
    lastSuggestions: null,
    profFiguresBySession: Object.fromEntries([
      ...Object.entries(s.profFiguresBySession).filter(([id]) => id !== sessionId),
      [sessionId, figureContext],
    ].slice(-20)),
    pendingProfFiguresByThread: state.chiediSessionId === sessionId
      ? s.pendingProfFiguresByThread
      : Object.fromEntries(Object.entries(s.pendingProfFiguresByThread).filter(([id]) => id !== PROF_THREAD_ID)),
    profFigureNoticesByThread: Object.fromEntries(Object.entries(s.profFigureNoticesByThread).filter(([id]) => id !== PROF_THREAD_ID)),
    profFigureImageStatusBySession: { ...s.profFigureImageStatusBySession, [sessionId]: 'not_loaded' },
    chiediSessionId: sessionId,
  }));

  return messages.length;
}

/** True when `head` is a positional prefix of `full` by message id. */
function isPrefixById(head: Message[], full: Message[]): boolean {
  if (head.length > full.length) return false;
  for (let i = 0; i < head.length; i += 1) {
    if (head[i].id !== full[i].id) return false;
  }
  return true;
}

function retainExplicitLocalOnlyTurns(
  serverMessages: Message[],
  localMessages: Message[],
): Message[] | null {
  const serverIds = new Set(serverMessages.map((message) => message.id));
  const localOnlyAssistantIndexes = localMessages.flatMap((message, index) => (
    message.authorKind === 'prof'
    && 'assistantPersistenceOutcome' in message
    && (
      message.assistantPersistenceOutcome === 'locally_retained_unsaved'
      || message.assistantPersistenceOutcome === 'failed'
    )
    && !serverIds.has(message.id)
      ? [index]
      : []
  ));
  if (localOnlyAssistantIndexes.length === 0) return null;

  const merged = [...serverMessages];
  for (const assistantIndex of localOnlyAssistantIndexes) {
    const assistant = localMessages[assistantIndex];
    if (!assistant || merged.some((message) => message.id === assistant.id)) continue;
    const precedingUser = localMessages[assistantIndex - 1];
    const matchingUserIndex = precedingUser?.authorKind === 'you' && 'text' in precedingUser
      ? findLastUserTextIndex(merged, precedingUser.text)
      : -1;
    if (matchingUserIndex >= 0) {
      merged.splice(matchingUserIndex + 1, 0, assistant);
      continue;
    }
    if (precedingUser && !merged.some((message) => message.id === precedingUser.id)) {
      merged.push(precedingUser);
    }
    merged.push(assistant);
  }
  return merged;
}

function findLastUserTextIndex(messages: Message[], text: string): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.authorKind === 'you' && 'text' in message && message.text === text) {
      return index;
    }
  }
  return -1;
}
